import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getPlantImage } from '../data/plantEmojis';
import ProductCard from '../components/ProductCard';
import { validateCoupon, getPublicCoupons, getShippingSettings } from '../lib/api';
import { computeShipping } from '../lib/shipping';
const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
import './CartPage.css';


function TruckIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/>
      <circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
      <path d="M10 11v6"/><path d="M14 11v6"/>
    </svg>
  );
}

function TagIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/>
      <line x1="7" y1="7" x2="7.01" y2="7"/>
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  );
}

function CouponModal({ open, onClose, onApply, appliedCoupon, total }) {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getPublicCoupons()
      .then(data => setCoupons(Array.isArray(data) ? data : []))
      .catch(() => setCoupons([]))
      .finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;
  return (
    <div className="cart-coupon-overlay" onClick={onClose}>
      <div className="cart-coupon-modal" onClick={e => e.stopPropagation()}>
        <div className="cart-coupon-modal__header">
          <h3 className="cart-coupon-modal__title"><TagIcon /> Available Coupons</h3>
          <button className="cart-coupon-modal__close" onClick={onClose}><CloseIcon /></button>
        </div>
        <div className="cart-coupon-modal__body">
          {loading && <p className="cart-coupon-empty">Loading coupons…</p>}
          {!loading && coupons.length === 0 && (
            <p className="cart-coupon-empty">No coupons available right now.</p>
          )}
          {!loading && coupons.map(c => {
            const isApplied = appliedCoupon?.code === c.code;
            const eligible = total >= (c.minOrder || 0);
            return (
              <div key={c.code} className={`cart-coupon-card${isApplied ? ' cart-coupon-card--applied' : ''}${!eligible ? ' cart-coupon-card--locked' : ''}`}>
                <div className="cart-coupon-card__left">
                  <div className="cart-coupon-card__tag"><TagIcon /> <strong>{c.code}</strong></div>
                  <div className="cart-coupon-card__label">{c.label}</div>
                  {c.desc && <div className="cart-coupon-card__desc">{c.desc}</div>}
                  {c.minOrder > 0 && (
                    <div className={`cart-coupon-card__min${eligible ? ' cart-coupon-card__min--ok' : ''}`}>
                      {eligible ? '✓ Eligible on your order' : `Min. order ₹${c.minOrder}`}
                    </div>
                  )}
                </div>
                <button
                  className={`cart-coupon-card__btn${isApplied ? ' cart-coupon-card__btn--applied' : ''}${!eligible ? ' cart-coupon-card__btn--locked' : ''}`}
                  disabled={!eligible}
                  onClick={() => { if (eligible) { onApply(c.code); onClose(); } }}
                >
                  {isApplied ? 'Applied ✓' : eligible ? 'Apply' : 'Locked'}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function CartPage({ cart, updateQty, removeFromCart, total, addToCart, appliedCoupon, setAppliedCoupon }) {
  const navigate = useNavigate();
  const [promoCode, setPromoCode]     = useState('');
  const [applying, setApplying]       = useState(false);
  const [couponModalOpen, setCouponModalOpen] = useState(false);
  const [promoError, setPromoError]   = useState('');
  const [promoSuccess, setPromoSuccess] = useState('');
  const [removingId, setRemovingId]   = useState(null);
  const [shippingSettings, setShippingSettings] = useState(null);
  const [recommended, setRecommended] = useState([]);
  const relatedTrackRef = useRef(null);

  useEffect(() => {
    getShippingSettings().then(setShippingSettings).catch(() => {});
  }, []);

  // Fetch recommended products from API based on cart categories
  useEffect(() => {
    const cartIds = cart.map(i => i.id || i.productId).filter(Boolean);
    fetch(`${BASE}/api/products?limit=12`)
      .then(r => r.json())
      .then(({ products }) => {
        setRecommended(
          (products || [])
            .filter(p => !cartIds.includes(String(p._id || p.id)))
            .slice(0, 8)
            .map(p => ({
              ...p,
              id: p._id || p.id,
              imageUrl: (p.images && p.images[0]?.url) || p.image || null,
            }))
        );
      })
      .catch(() => {});
  }, []);

  const scrollRelated = (dir) => {
    const el = relatedTrackRef.current;
    if (!el) return;
    const cardWidth = el.querySelector('.cart-recs__card')?.offsetWidth || 220;
    el.scrollBy({ left: dir * (cardWidth + 16), behavior: 'smooth' });
  };

  const cartCount      = cart.reduce((s, i) => s + i.qty, 0);
  const discount       = appliedCoupon?.discountAmount || 0;
  const { amount: shipping, label: shippingLabel } = computeShipping(shippingSettings, { subtotal: total });
  const finalTotal     = Math.max(0, total - discount) + shipping;
  const freeShippingThreshold = shippingSettings?.freeShippingEnabled ? shippingSettings.freeShippingThreshold : null;
  const progress       = freeShippingThreshold ? Math.min((total / freeShippingThreshold) * 100, 100) : 100;
  const toFreeShipping = freeShippingThreshold ? Math.max(freeShippingThreshold - total, 0) : 0;

  // Keep input synced with whatever coupon is currently applied (e.g. applied from cart drawer)
  useEffect(() => {
    if (appliedCoupon) {
      setPromoCode(appliedCoupon.code);
      setPromoSuccess(`${appliedCoupon.label || appliedCoupon.code} applied!`);
      setPromoError('');
    } else {
      setPromoCode('');
      setPromoSuccess('');
    }
  }, [appliedCoupon]);

  const handleApplyPromo = async (overrideCode) => {
    const code = (overrideCode || promoCode).trim().toUpperCase();
    if (!code) return;
    setApplying(true);
    setPromoError('');
    setPromoSuccess('');
    try {
      const result = await validateCoupon(code, total);
      setAppliedCoupon({ ...result, desc: result.desc || '' });
      setPromoCode(result.code);
      setPromoSuccess(`${result.label || result.code} applied!`);
    } catch (err) {
      setPromoError(err.message || 'Invalid coupon code.');
      setAppliedCoupon(null);
    } finally {
      setApplying(false);
    }
  };

  const handleRemovePromo = () => {
    setAppliedCoupon(null);
    setPromoCode('');
    setPromoError('');
    setPromoSuccess('');
  };

  const handleRemove = (id) => {
    setRemovingId(id);
    setTimeout(() => { removeFromCart(id); setRemovingId(null); }, 320);
  };

  /* ── Empty state ── */
  if (cart.length === 0) {
    return (
      <main className="cart-page">
        <div className="cart-page__inner">
          <div className="cart-empty">
            <div className="cart-empty__pot">
              <svg viewBox="0 0 120 140" fill="none">
                <ellipse cx="60" cy="125" rx="32" ry="8" fill="#D8F3DC"/>
                <path d="M30 60 L36 118 Q36 125 43 125 H77 Q84 125 84 118 L90 60Z" fill="#B7E4C7" stroke="#74C69D" strokeWidth="1.5"/>
                <rect x="22" y="54" width="76" height="12" rx="6" fill="#74C69D"/>
                <path d="M60 54 C60 54 60 20 80 10 C80 10 75 35 60 54Z" fill="#40916C" fillOpacity="0.5"/>
                <path d="M60 54 C60 54 60 15 40 8 C40 8 48 38 60 54Z" fill="#2D6A4F" fillOpacity="0.4"/>
                <path d="M60 54 C60 54 72 30 85 28 C85 28 78 48 60 54Z" fill="#40916C" fillOpacity="0.3"/>
              </svg>
            </div>
            <h1 className="cart-empty__title">Your garden awaits</h1>
            <p className="cart-empty__sub">Add some green companions to get started</p>
            <Link to="/collections/plants" className="cart-empty__cta">Explore Plants</Link>
          </div>
        </div>
      </main>
    );
  }

  /* ── Summary sidebar (shared JSX) ── */
  const summaryPanel = (
    <div className="cart-summary__inner">
      <h2 className="cart-summary__title">Order Summary</h2>

      {/* Promo */}
      <div className="cart-promo">
        {!appliedCoupon ? (
          <div className="cart-promo__row">
            <input
              className="cart-promo__input"
              type="text"
              placeholder="Promo code"
              value={promoCode}
              onChange={e => { setPromoCode(e.target.value.toUpperCase()); setPromoError(''); setPromoSuccess(''); }}
              onKeyDown={e => e.key === 'Enter' && handleApplyPromo()}
            />
            <button className="cart-promo__btn" onClick={() => handleApplyPromo()} disabled={applying || !promoCode.trim()}>
              {applying ? '…' : 'Apply'}
            </button>
          </div>
        ) : (
          <div className="cart-promo__applied-row">
            <span className="cart-promo__msg cart-promo__msg--ok">✓ {promoSuccess}</span>
            <button className="cart-promo__remove" onClick={handleRemovePromo}>Remove</button>
          </div>
        )}
        {promoError && <p className="cart-promo__msg cart-promo__msg--err">{promoError}</p>}
        <button className="cart-promo__view-all" onClick={() => setCouponModalOpen(true)}>
          <TagIcon /> View all coupons
        </button>
        <CouponModal
          open={couponModalOpen}
          onClose={() => setCouponModalOpen(false)}
          onApply={(code) => { setPromoCode(code); handleApplyPromo(code); }}
          appliedCoupon={appliedCoupon}
          total={total}
        />
      </div>

      {/* Lines */}
      <div className="cart-summary__lines">
        <div className="cart-summary__line">
          <span>Subtotal ({cartCount} {cartCount === 1 ? 'item' : 'items'})</span>
          <span>₹{total.toLocaleString()}</span>
        </div>
        {discount > 0 && (
          <div className="cart-summary__line cart-summary__line--disc">
            <span>Discount ({appliedCoupon.code})</span>
            <span>−₹{discount.toLocaleString()}</span>
          </div>
        )}
        <div className="cart-summary__line">
          <span>Shipping</span>
          <span className={shipping === 0 ? 'free' : ''}>
            {shipping === 0 ? 'Free' : `₹${shipping}`}
          </span>
        </div>
      </div>

      <div className="cart-summary__divider" />

      <div className="cart-summary__total">
        <span>Total</span>
        <span>₹{finalTotal.toLocaleString()}</span>
      </div>

      <button className="cart-summary__checkout" onClick={() => navigate('/checkout')}>
        Checkout
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
        </svg>
      </button>

      <div className="cart-trust">
        <span>🔒 Secure checkout</span>
        <span>·</span>
        <span>↩ Easy returns</span>
        <span>·</span>
        <span>🚚 2–3 days</span>
      </div>
    </div>
  );
  return (
    <main className="cart-page">
      <div className="cart-page__inner">

        {/* Header */}
        <div className="cart-page__header">
          <Link to="/collections/plants" className="cart-page__back">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
            Continue shopping
          </Link>
          <div className="cart-page__heading">
            <h1 className="cart-page__title">My Cart</h1>
            <span className="cart-page__count">{cartCount}</span>
          </div>
        </div>

        {/* Shipping progress */}
        {freeShippingThreshold !== null && (
          <div className={`cart-ship${toFreeShipping === 0 ? ' cart-ship--done' : ''}`}>
            <TruckIcon />
            {toFreeShipping > 0
              ? <span>Add <strong>₹{toFreeShipping.toLocaleString()}</strong> more for free shipping</span>
              : <span><strong>Free shipping unlocked!</strong> 🎉</span>
            }
            <div className="cart-ship__bar">
              <div className="cart-ship__fill" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}

        {/* Layout: items left, summary right */}
        <div className="cart-layout">

          {/* ── Items ── */}
          <section className="cart-items">
            {cart.map((item, idx) => (
              <div
                key={item.cartItemId || item.id}
                className={`cart-card${removingId === (item.cartItemId || item.id) ? ' cart-card--out' : ''}`}
                style={{ animationDelay: `${idx * 0.06}s` }}
              >
                {/* Image */}
                {item.slug ? (
                  <Link
                    to={`/product/${item.slug}${item._variantId ? `?variant=${item._variantId}` : ''}`}
                    className="cart-card__img cart-card__img--link"
                    aria-label={`View ${item.name}`}
                    tabIndex={-1}
                  >
                    <img src={item.imageUrl || getPlantImage(item.image)} alt={item.name} />
                  </Link>
                ) : (
                  <div className="cart-card__img">
                    <img src={item.imageUrl || getPlantImage(item.image)} alt={item.name} />
                  </div>
                )}

                {/* Details */}
                <div className="cart-card__body">
                  <div className="cart-card__top">
                    <div className="cart-card__meta">
                      {item.slug ? (
                        <Link
                          to={`/product/${item.slug}${item._variantId ? `?variant=${item._variantId}` : ''}`}
                          className="cart-card__name cart-card__name--link"
                        >
                          {item.name}
                        </Link>
                      ) : (
                        <h3 className="cart-card__name">{item.name}</h3>
                      )}

                      {/* Pot / variant */}
                      {!item.type || item.type !== 'bundle' ? (
                        <p className="cart-card__attr">
                          <span className="cart-card__attr-val">{[item._selectedSize, item._selectedPlanter, item._selectedColor].filter(Boolean).join(' · ')}</span>
                        </p>
                      ) : null}

                      {/* Bundle selected plants */}
                      {item.type === 'bundle' && item.bundleSelectedProducts?.length > 0 && (
                        <div className="cart-bundle-items">
                          {item.bundleSelectedProducts.map((p, pi) => {
                            const variants = [p.sizeLabel, p.planterName, p.colorLabel].filter(Boolean).join(' · ');
                            return (
                              <div key={pi} className="cart-bundle-item">
                                {p.imageUrl
                                  ? <img src={p.imageUrl} alt={p.name} className="cart-bundle-item__img" />
                                  : <span className="cart-bundle-item__fallback">🌿</span>
                                }
                                <span className="cart-bundle-item__info">
                                  <span className="cart-bundle-item__name">{p.name}</span>
                                  {variants && <span className="cart-bundle-item__variant">{variants}</span>}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}

                    </div>

                    {/* Remove — top right */}
                    <button className="cart-card__remove" onClick={() => handleRemove(item.cartItemId || item.id)} aria-label="Remove">
                      <TrashIcon />
                    </button>
                  </div>

                  {/* Price row + qty */}
                  <div className="cart-card__bottom">
                    {item.type === 'bundle' ? (
                      <span style={{ fontSize: '.8rem', color: '#6b7280' }}>Bundle · qty 1</span>
                    ) : (
                      <div className="cart-card__qty">
                        <button onClick={() => updateQty(item.cartItemId || item.id, item.qty - 1)} aria-label="Decrease">−</button>
                        <span>{item.qty}</span>
                        <button onClick={() => updateQty(item.cartItemId || item.id, item.qty + 1)} aria-label="Increase">+</button>
                      </div>
                    )}
                    <p className="cart-card__price">₹{(item.price * item.qty).toLocaleString()}</p>
                  </div>
                </div>
              </div>
            ))}
          </section>

          {/* ── Summary sidebar ── */}
          <aside className="cart-summary">
            {summaryPanel}
          </aside>
        </div>

        {/* Mobile sticky checkout */}
        <div className="cart-mobile-bar">
          <div className="cart-mobile-bar__total">
            <span>Total</span>
            <strong>₹{finalTotal.toLocaleString()}</strong>
          </div>
          <button className="cart-mobile-bar__btn" onClick={() => navigate('/checkout')}>
            Checkout →
          </button>
        </div>

        {/* Recommendations */}
        {recommended.length > 0 && (
          <section className="cart-recs">
            <div className="cart-recs__hd">
              <h2 className="cart-recs__title">You might also like</h2>
              <div className="cart-recs__nav">
                <button type="button" className="cart-recs__nav-btn" onClick={() => scrollRelated(-1)} aria-label="Scroll left">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                </button>
                <button type="button" className="cart-recs__nav-btn" onClick={() => scrollRelated(1)} aria-label="Scroll right">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                </button>
              </div>
            </div>
            <div className="cart-recs__grid" ref={relatedTrackRef}>
              {recommended.map(p => (
                <div key={p.id || p._id} className="cart-recs__card">
                  <ProductCard product={p} addToCart={addToCart} />
                </div>
              ))}
            </div>
          </section>
        )}

      </div>
    </main>
  );
}